import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1",
  timeout: 10000,
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get("token") || localStorage.getItem("accessToken");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't try to refresh token for auth endpoints to avoid infinite loops
    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/logout");

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshResponse = await api.post("/auth/refresh");
        const { accessToken } = refreshResponse.data.data;

        // Update stored token
        localStorage.setItem("accessToken", accessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        Cookies.remove("token");
        Cookies.remove("refreshToken");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("user");

        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors - don't show toast for auth endpoints to avoid duplicates
    const isAuthErrorEndpoint =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/logout");

    if (!isAuthErrorEndpoint) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Une erreur inattendue s'est produite");
      }
    }

    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  logout: () => api.post("/auth/logout"),
  getCurrentUser: () => api.get("/auth/me"),
  refreshToken: () => api.post("/auth/refresh"),
  changePassword: (data) => api.patch("/auth/change-password", data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (data) => api.post("/auth/reset-password", data),
};

export const userAPI = {
  getProfile: () => api.get("/user/profile"),
  updateProfile: (data) => {
    // Check if data is FormData (for photo upload)
    if (data instanceof FormData) {
      return api.patch("/user/profile", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.patch("/user/profile", data);
  },
  updatePassword: (data) => api.patch("/user/password", data),
  updateNotifications: (data) => api.patch("/user/notifications", data),
  uploadProfilePhoto: (formData) =>
    api.post("/user/profile/photo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  deleteProfilePhoto: () => api.delete("/user/profile/photo"),
};

export const employeesAPI = {
  getAll: (params) => api.get("/employees", { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => {
    // Check if data is FormData (for file upload)
    if (data instanceof FormData) {
      return api.post("/employees", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.post("/employees", data);
  },
  update: (id, data) => {
    // Validate ID
    if (!id || id === "undefined" || id === "null") {
      console.error("Invalid employee ID for update:", id);
      return Promise.reject(new Error("Invalid employee ID"));
    }

    // Check if data is FormData (for file upload)
    if (data instanceof FormData) {
      return api.patch(`/employees/${id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.patch(`/employees/${id}`, data);
  },
  delete: (id) => api.delete(`/employees/${id}`),
  toggleStatus: (id, status) =>
    api.patch(`/employees/${id}/activate`, { statut: status }),
  downloadTemplate: () =>
    api.get("/employees/template", {
      responseType: "blob",
    }),
  export: (params) =>
    api.get("/employees/export", {
      params,
      responseType: "blob",
    }),
  import: (formData) =>
    api.post("/employees/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
};

export const departmentsAPI = {
  getAll: (params) => api.get("/departments", { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post("/departments", data),
  update: (id, data) => api.patch(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

export const attendanceAPI = {
  // Clock in/out operations
  clockIn: (data) => api.post("/attendance/clock-in", data),
  clockOut: (data) => api.post("/attendance/clock-out", data),
  recordBreak: (data) => api.post("/attendance/break", data),

  // Status and history
  getCurrentStatus: () => api.get("/attendance/current-status"),
  getHistory: (employeeId, params) =>
    api.get(`/attendance/${employeeId}`, { params }),
  getMyAttendanceHistory: (params) =>
    api.get("/attendance/my-attendance", { params }),

  // Dashboard and statistics
  getDashboard: (params) => api.get("/attendance/dashboard", { params }),
  getEnhancedDashboard: (params) =>
    api.get("/attendance/enhanced-dashboard", { params }),

  // Absence management
  createAbsenceRequest: (data) => {
    if (data instanceof FormData) {
      return api.post("/attendance/absences", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.post("/attendance/absences", data);
  },
  getAbsenceRequests: (params) => api.get("/attendance/absences", { params }),
  getAbsenceById: (id) => api.get(`/attendance/absences/${id}`),
  deleteAbsenceRequest: (id) => api.delete(`/attendance/absences/${id}`),
  approveAbsenceRequest: (id, data) =>
    api.put(`/attendance/absences/${id}/approve`, data),
  downloadAbsenceJustification: (id) =>
    api.get(`/attendance/absences/${id}/justification`, {
      responseType: "blob",
    }),

  // Absence types
  getAbsenceTypes: () => api.get("/attendance/absence-types"),
};

export const leaveAPI = {
  // Leave requests
  getRequests: (params) => api.get("/leave/requests", { params }),
  getRequestCount: () => api.get("/leave/requests/count"), // Total count of requests
  getMyRequests: () => api.get("/leave/requests"), // Employee's own requests
  createRequest: (data) => {
    // Check if data is FormData (for file upload)
    if (data instanceof FormData) {
      return api.post("/leave/requests", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.post("/leave/requests", data);
  },
  updateRequest: (id, data) => {
    // Check if data is FormData (for file upload)
    if (data instanceof FormData) {
      return api.put(`/leave/requests/${id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.put(`/leave/requests/${id}`, data);
  },
  deleteRequest: (id) => api.delete(`/leave/requests/${id}`),
  approveRequest: (id, data) => api.put(`/leave/requests/${id}/approve`, data),

  // Leave types
  getTypes: () => api.get("/leave/types"),
  getLeaveTypes: () => api.get("/leave/types"), // Alias for consistency

  // Leave balance
  getBalance: (employeeId, params) =>
    api.get(`/leave/balance/${employeeId}`, { params }),
  getMyBalance: () => api.get("/leave/my-balance"), // Employee's own balance

  // Leave calendar
  getCalendar: (params) => api.get("/leave/calendar", { params }),

  // Export leave data
  exportData: (params) => api.get("/leave/export", { params }),

  // Download justification file
  downloadJustification: (id) =>
    api.get(`/leave/requests/${id}/justification`, {
      responseType: "blob", // Important for file downloads
    }),

  // Legacy support (keep for backward compatibility)
  getAll: (params) => api.get("/leave/requests", { params }),
  create: (data) => {
    // Check if data is FormData (for file upload)
    if (data instanceof FormData) {
      return api.post("/leave/requests", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    }
    return api.post("/leave/requests", data);
  },
  approve: (id, data) => api.put(`/leave/requests/${id}/approve`, data),
  reject: (id, data) => api.put(`/leave/requests/${id}/approve`, data), // Same endpoint, different status
};

export const payrollAPI = {
  // Payslips
  getPayslips: (params) => api.get("/payroll/payslips", { params }),
  calculatePayroll: (data) => api.post("/payroll/calculate", data),

  // Expense claims
  getExpenses: (params) => api.get("/payroll/expenses", { params }),
  createExpense: (data) => api.post("/payroll/expenses", data),
  getExpenseReceipt: (id) =>
    api.get(`/payroll/expenses/${id}/receipt`, { responseType: "blob" }),
  updateExpense: (id, data) => api.put(`/payroll/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/payroll/expenses/${id}`),
  approveExpense: (id, data) =>
    api.put(`/payroll/expenses/${id}/approve`, data),

  // Reports
  getReports: (params) => api.get("/payroll/reports", { params }),

  // Update and Delete
  updatePayslip: (id, data) => api.put(`/payroll/payslips/${id}`, data),
  deletePayslip: (id) => api.delete(`/payroll/payslips/${id}`),

  // Export
  exportPayslips: (params) =>
    api.get("/payroll/payslips/export", {
      params,
      responseType: "blob",
    }),
  exportExpenses: (params) =>
    api.get("/payroll/expenses/export", {
      params,
      responseType: "blob",
    }),
  exportReports: (params) =>
    api.get("/payroll/reports/export", {
      params,
      responseType: "blob",
    }),
};

export const recruitmentAPI = {
  // Job Offers
  getJobs: (params) => api.get("/recruitment/jobs", { params }),
  getJobById: (id) => api.get(`/recruitment/jobs/${id}`),
  createJob: (data) => api.post("/recruitment/jobs", data),
  updateJob: (id, data) => api.put(`/recruitment/jobs/${id}`, data),
  publishJob: (id) => api.patch(`/recruitment/jobs/${id}/publish`),
  deleteJob: (id) => api.delete(`/recruitment/jobs/${id}`),

  // Applications
  getApplications: (params) => api.get("/recruitment/applications", { params }),
  getCandidatures: (params) => api.get("/recruitment/applications", { params }),
  getApplicationById: (id) => api.get(`/recruitment/applications/${id}`),
  submitApplication: (data) => api.post("/recruitment/applications", data),
  updateApplication: (id, data) =>
    api.put(`/recruitment/applications/${id}`, data),
  deleteApplication: (id) => api.delete(`/recruitment/applications/${id}`),
  downloadApplicationFile: (id, fileType) =>
    api.get(`/recruitment/applications/${id}/download/${fileType}`, {
      responseType: "blob",
    }),
  evaluateApplication: (id, data) =>
    api.patch(`/recruitment/applications/${id}/evaluate`, data),

  // Interviews
  getInterviews: (params) => api.get("/recruitment/interviews", { params }),
  scheduleInterview: (data) => api.post("/recruitment/interviews", data),
  updateInterview: (id, data) =>
    api.patch(`/recruitment/interviews/${id}`, data),

  // Dashboard
  getDashboard: () => api.get("/recruitment/dashboard"),
};

export const trainingAPI = {
  getAll: (params) => api.get("/training", { params }),
  getById: (id) => api.get(`/training/${id}`),
  create: (data) => api.post("/training", data),
  update: (id, data) => api.put(`/training/${id}`, data),
  delete: (id) => api.delete(`/training/${id}`),
  enroll: (formationId, data) =>
    api.post(`/training/${formationId}/enroll`, data),
  getMyEnrollments: (params) => api.get("/training/my-enrollments", { params }),
  updateEnrollmentStatus: (inscriptionId, data) =>
    api.put(`/training/enrollments/${inscriptionId}`, data),
  getStats: () => api.get("/training/stats"),
};

export const filesAPI = {
  upload: (formData) =>
    api.post("/files/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  get: (id) => api.get(`/files/${id}`),
  delete: (id) => api.delete(`/files/${id}`),
};

export const notificationsAPI = {
  getAll: (params) => api.get("/notifications", { params }),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Department API
export const departmentAPI = {
  getAll: (params) => api.get("/departments", { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post("/departments", data),
  update: (id, data) => api.patch(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  hardDelete: (id) => api.delete(`/departments/${id}?hardDelete=true`),
  getAvailableManagers: () => api.get("/departments/managers"),
  getStats: (id) => api.get(`/departments/${id}/stats`),
};

// Interview API
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
  getActivities: () => api.get("/dashboard/activities"),
  getDepartmentStats: () => api.get("/dashboard/departments"),
  getAttendanceChart: () => api.get("/dashboard/attendance-chart"),
  getUserDashboard: () => api.get("/dashboard/user"),
  getHealthcareMetrics: () => api.get("/dashboard/healthcare-metrics"),
};

export const interviewAPI = {
  getAll: (params) => api.get("/interviews", { params }),
  getById: (id) => api.get(`/interviews/${id}`),
  create: (data) => api.post("/interviews", data),
  update: (id, data) => api.put(`/interviews/${id}`, data),
  delete: (id) => api.delete(`/interviews/${id}`),
  getStats: () => api.get("/interviews/stats"),
};

export default api;
